import React, { useState } from "react";

import _ from "lodash";

import Icon from "shared/components/Icon";
import { ActionLink } from "shared/components/Links";
import Well from "shared/components/Well";

function getOutcomeIcon(outcome) {
  switch (outcome?.scoreLabelColor) {
    case "fail":
      return Icon.CrossRedSmall;
    case "warn":
      return Icon.OrangeExclamation;
    case "pass":
    default:
      return Icon.CheckGreenSmall;
  }
}

// Helper to get border color *value* based on severity
function getBorderColorValue(outcome) {
  switch (outcome?.scoreLabelColor) {
    case "fail":
      // Attempt to use CSS variable, fallback to hex
      return "var(--vega-color-critical, #d9534f)";
    case "warn":
      return "var(--vega-color-warning, #f0ad4e)";
    case "pass":
    default:
      return "var(--vega-color-success, #5cb85c)";
  }
}

// Subtle background tint per severity
function getBackgroundColorValue(outcome) {
  switch (outcome?.scoreLabelColor) {
    case "fail":
      return "rgba(217, 83, 79, 0.05)"; // light red
    case "warn":
      return "rgba(240, 173, 78, 0.05)"; // light orange
    case "pass":
    default:
      return "rgba(92, 184, 92, 0.05)"; // light green
  }
}

// Helper function to check for valid recommendations
const hasValidRecommendations = (recommendations) =>
  // Check if the array exists, is not empty, and has at least one non-empty string
  !_.isEmpty(recommendations) &&
  _.some(recommendations, (r) => _.isString(r) && r.trim() !== "");

// Helper function to strip priority prefix from score labels (e.g., "p0-INCOME REVIEW: NEEDS REVIEW" → "INCOME REVIEW: NEEDS REVIEW")
const stripPriorityPrefix = (scoreLabel) => {
  if (!scoreLabel) return scoreLabel;
  return scoreLabel.replace(/^p\d+-/, "");
};

const OutcomesV2 = ({ lumenRun }) => {
  // Ensure lumenRun and necessary nested properties exist
  if (
    !lumenRun?.outcomes ||
    !lumenRun?.settings?.outcomes ||
    !lumenRun?.results
  ) {
    return null;
  }

  // 1. Get Data
  const topLevelOutcomes = lumenRun.outcomes;
  const allEntries = _.flatMap(
    lumenRun.results,
    (result) => result?.evaluation?.history || []
  );

  // Helper: build a lookup of formulas (pre-colon) → entry for quick match
  const entryLookup = _.chain(allEntries)
    .filter((e) => typeof e?.formula === "string")
    .keyBy((e) => e.formula.split(":")[0])
    .value();

  // 2. Process Outcomes & Find Entries
  const rawOutcomesWithEntries = _.chain(topLevelOutcomes)
    // 2a. Focus only on warn/fail severities
    .filter(
      (outcome) =>
        outcome.scoreLabelColor === "warn" || outcome.scoreLabelColor === "fail"
    )
    // 2b. Grab matching evaluation entry (if any) via outcome.name
    .map((outcome) => ({
      outcome,
      entry: entryLookup[outcome.name] || null,
    }))
    .value();

  // 2e. Group by outcome scoreLabel to deduplicate based on what's displayed
  const groupedByScoreLabel = _.groupBy(
    rawOutcomesWithEntries,
    (item) => item.outcome.scoreLabel // Still group by the full scoreLabel to maintain unique grouping
  );

  const outcomesWithEntries = _.map(groupedByScoreLabel, (items) => ({
    ...items[0], // Take the first item's data (outcome, entry)
    // Keep the first item found for each unique score label
  }));

  // 3. Severity Sorting
  const severityOrder = { fail: 1, warn: 2, pass: 3 }; // Define order
  const sortedOutcomesWithEntries = _.sortBy(outcomesWithEntries, (item) => {
    return severityOrder[item.outcome.scoreLabelColor] || 99; // Sort by severity
  });

  // Generate a stable key for each item based on its score label
  const getItemKey = (item) => item.outcome.scoreLabel; // Keep using full scoreLabel for keys

  // Filter items *with* recommendations for accordion logic
  const itemsWithRecs = _.filter(sortedOutcomesWithEntries, (item) =>
    hasValidRecommendations(item.outcome.recommendations)
  );

  // State for Accordion Items - Initialize with the first item *with recommendations* expanded
  const [expandedKeys, setExpandedKeys] = useState(() => {
    if (itemsWithRecs.length > 0) {
      const firstItemKey = getItemKey(itemsWithRecs[0]); // Use scoreLabel key here too
      return { [firstItemKey]: true };
    }
    return {};
  });

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // 4. Render
  if (_.isEmpty(sortedOutcomesWithEntries)) {
    return null;
  }

  return (
    <div
      className="vega content flex column gap"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      {/* Render all items uniformly */}
      {_.map(sortedOutcomesWithEntries, (item) => {
        const itemKey = getItemKey(item);
        const hasRecs = hasValidRecommendations(item.outcome.recommendations);
        // Only items with recommendations can be expanded/collapsed
        const isExpanded = hasRecs ? !!expandedKeys[itemKey] : false;
        const OutcomeIcon = getOutcomeIcon(item.outcome);
        const title =
          stripPriorityPrefix(item.outcome.scoreLabel) ||
          item.outcome.scoreLabel;
        const borderTopColorValue = getBorderColorValue(item.outcome);
        // const showDescription = !!item.entry?.explanation?.output?.description;

        return (
          <div
            key={itemKey}
            className="vega card outline padding large flex column gap"
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: "100%",
              boxShadow:
                "0 2px 4px rgba(0, 0, 0, 0.05), 0 4px 8px rgba(0, 0, 0, 0.05)",
              borderRadius: "0.5rem",
              borderTopWidth: "6px",
              borderTopStyle: "solid",
              borderTopColor: borderTopColorValue,
              backgroundColor: getBackgroundColorValue(item.outcome),
            }}
          >
            {/* Wrapper for Header + Content to allow Link to be pushed down */}
            <div style={{ flexGrow: 1 }} className="vega flex column gap-small">
              {/* Static Header - NOT Clickable */}
              <div className="vega flex no-wrap gap items-center justify-between">
                <div className="vega flex no-wrap gap items-center">
                  <OutcomeIcon className="vega icon large" />
                  <span
                    className="vega title small"
                    style={{ alignSelf: "center" }}
                  >
                    {title}
                  </span>
                </div>
              </div>

              {/* Content Area: Description + Conditional Recommendations */}
              <div
                className="vega flex column gap-small"
                style={{ marginTop: "0.5rem" }}
              >
                {/* Description - Always show if available */}
                {/* {showDescription && (
                  <p className="vega info">
                    {item.entry.explanation.output.description}
                  </p>
                )} */}

                {/* Recommendations Section - Show only if recs exist and are expanded */}
                {hasRecs && isExpanded && (
                  <>
                    <div className="vega flex column gap-small">
                      {_.map(
                        _.filter(
                          item.outcome.recommendations,
                          (r) => _.isString(r) && r.trim() !== ""
                        ),
                        (r, index) => (
                          <Well key={index} className="vega info subtle-bg">
                            {r}
                          </Well>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>{" "}
            {/* Show/Hide Details Link Wrapper - Only render if there are recommendations */}
            {hasRecs && (
              <div
                style={{
                  display: "flex", // Make the wrapper a flex container
                  justifyContent: "flex-end", // Align content (the link) to the end (right)
                  marginTop: "auto", // Push this wrapper to the bottom
                }}
              >
                <ActionLink
                  onClick={() => toggleExpand(itemKey)}
                  style={{ color: getBorderColorValue(item.outcome) }}
                >
                  {isExpanded ? "Hide Details" : "Details"}
                </ActionLink>
              </div>
            )}
          </div>
        );
      })}
      {/* Removed the separate container for remaining items */}
    </div>
  );
};

export default OutcomesV2;
